import type { PlaybackTime as SDKPlaybackTime } from '@speechifyinc/multiplatform-sdk';

import { logEventInObservability } from 'lib/observability';
import { HIGHLIGHT_BORDER_RADIUS } from 'modules/listening/features/reader/components/classicV3/BlockOverlays';

import { MultiplatformSDKInstance } from '../../sdk';
import { SDKFacade } from '../_base';
import { PlaybackTime } from '../playback';

export class SDKSingletonCommonReaderFacade extends SDKFacade {
  private static _singleton: SDKSingletonCommonReaderFacade;

  constructor(sdk: MultiplatformSDKInstance) {
    super(sdk);
    SDKSingletonCommonReaderFacade._singleton = this;
  }

  static override get singleton(): SDKSingletonCommonReaderFacade {
    return SDKSingletonCommonReaderFacade._singleton;
  }

  get PlaybackTime() {
    return this.sdk.sdkModule.PlaybackTime;
  }

  get PlayPauseButton() {
    return this.sdk.sdkModule.PlayPauseButton;
  }

  sdkPlaybackTimeToOurPlaybackTime(sdkPlaybackTime: SDKPlaybackTime): PlaybackTime {
    if (sdkPlaybackTime instanceof this.PlaybackTime.Ready) {
      return {
        isLoading: false,
        currentTimeSeconds: sdkPlaybackTime.currentTimeSeconds,
        totalTimeSeconds: sdkPlaybackTime.totalTimeSeconds
      };
    }

    return {
      isLoading: true
    };
  }

  get Polygon() {
    return this.sdk.sdkModule.Polygon;
  }

  get Box() {
    return this.sdk.sdkModule.Box;
  }

  get Point() {
    return this.sdk.sdkModule.Point;
  }

  // SDK uses integers internally so if we want sub-pixel precision we need to multiply the pixel values
  private readonly _pxMultiplier = 100;
  toSDKPixels(valuePx: number) {
    return Math.round(valuePx * this._pxMultiplier);
  }
  fromSDKPixels(valueSDKPx: number) {
    return valueSDKPx / this._pxMultiplier;
  }

  private _getBoxFromRect(rect: DOMRect) {
    return new this.sdk.sdkModule.Box(
      new this.sdk.sdkModule.Point(
        //
        this.toSDKPixels(rect.left),
        this.toSDKPixels(rect.top)
      ),
      // We need to make sure that the box size does not get rounded to 0
      Math.max(1, this.toSDKPixels(rect.width)),
      Math.max(1, this.toSDKPixels(rect.height))
    );
  }

  private _getVerticesFromRects(rects: DOMRect[]) {
    const boxes = rects.map(rect => this._getBoxFromRect(rect));
    const polygon = new this.sdk.sdkModule.Polygon(boxes);
    return polygon.vertices.map(v => ({ x: this.fromSDKPixels(v.x), y: this.fromSDKPixels(v.y) }));
  }

  getSvgPathFromRegions(rects: DOMRect[]) {
    const vertices = this._getVerticesFromRects(rects);

    if (vertices.length < 3) {
      console.warn('Not enough points to form a polygon with corners', vertices, rects);
      logEventInObservability('NOT_ENOUGH_POINTS_TO_FORM_A_POLYGON_WITH_CORNERS', {
        length: String(vertices.length),
        rects: JSON.stringify(rects.map(r => ({ left: r.left, top: r.top, width: r.width, height: r.height })))
      });
      // FIXME: This sometimes happens but I'm not able to reliably reproduce it
      // It happens whe there is one small region being rendered, could be potentially issue with the Polygon API
      return '';
    }

    const path: string[] = [];
    const n = vertices.length;

    for (let i = 0; i < n; i++) {
      const current = vertices[i];
      const next = vertices[(i + 1) % n];
      const prev = vertices[(i - 1 + n) % n];

      // Calculate direction vectors
      const toPrev = { x: prev.x - current.x, y: prev.y - current.y };
      const toNext = { x: next.x - current.x, y: next.y - current.y };

      // Normalize vectors
      const toPrevLength = Math.sqrt(toPrev.x * toPrev.x + toPrev.y * toPrev.y);
      const toNextLength = Math.sqrt(toNext.x * toNext.x + toNext.y * toNext.y);

      const toPrevNorm = {
        x: toPrev.x / toPrevLength,
        y: toPrev.y / toPrevLength
      };
      const toNextNorm = {
        x: toNext.x / toNextLength,
        y: toNext.y / toNextLength
      };

      // Calculate arc start and end points
      const arcStartPoint = {
        x: current.x + toPrevNorm.x * Math.min(HIGHLIGHT_BORDER_RADIUS, toPrevLength / 2),
        y: current.y + toPrevNorm.y * Math.min(HIGHLIGHT_BORDER_RADIUS, toPrevLength / 2)
      };

      const arcEndPoint = {
        x: current.x + toNextNorm.x * Math.min(HIGHLIGHT_BORDER_RADIUS, toNextLength / 2),
        y: current.y + toNextNorm.y * Math.min(HIGHLIGHT_BORDER_RADIUS, toNextLength / 2)
      };

      // Navigate to start of the arc
      path.push(i === 0 ? 'M' : 'L', [arcStartPoint.x, arcStartPoint.y].join(','));

      // Do the arc using bezier curve
      path.push('Q', [current.x, current.y, arcEndPoint.x, arcEndPoint.y].join(','));
    }

    path.push('Z');
    return path.join(' ');
  }
}
